// apps/app/src/lib/api-client.ts
//
// One client for the dashboard. Access tokens live in memory only — never
// localStorage, where any XSS on the page can read them. The refresh token is an
// httpOnly cookie the JavaScript cannot touch at all.

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.skinvizo.com/v1';

let accessToken: string | null = null;
let refreshing: Promise<RefreshOutcome> | null = null;
const onUnauthenticated = new Set<() => void>();

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level errors, so the form can render them under the right input. */
  get fieldErrors(): Record<string, string> {
    return (this.details?.['fields'] as Record<string, string>) ?? {};
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function onSignedOut(handler: () => void): () => void {
  onUnauthenticated.add(handler);
  return () => onUnauthenticated.delete(handler);
}

/**
 * What a refresh attempt established about the session.
 *
 * Only `invalid` — the server answering 401/403 — means the session is over.
 * A rate limit, a 5xx or a dropped connection says nothing about the session,
 * and treating them as "signed out" logged people out of a valid session
 * whenever the API was briefly busy.
 */
export type RefreshOutcome = 'ok' | 'invalid' | 'unavailable';

const MAX_REFRESH_ATTEMPTS = 4;

/**
 * One refresh at a time across every tab, not just this one. Two tabs whose
 * access expires together would each present the same refresh token; the
 * second reads as reuse, and reuse detection signs the person out everywhere —
 * for having the dashboard open twice. Inside the lock, the second tab's
 * request carries the cookie the first one just rotated, and succeeds.
 */
function acrossTabs<T>(work: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('gs-session-refresh', work) as Promise<T>;
  }
  return work();
}

export function refreshSession(): Promise<RefreshOutcome> {
  // One refresh at a time. Five parallel 401s would otherwise start five
  // rotations, and reuse detection would revoke the whole family — signing the
  // user out for being busy.
  refreshing ??= acrossTabs(async () => {
    try {
      for (let attempt = 1; ; attempt++) {
        let response: Response;
        try {
          response = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
        } catch {
          if (attempt >= MAX_REFRESH_ATTEMPTS) return 'unavailable';
          await wait(backoff(attempt));
          continue;
        }

        if (response.ok) {
          const body = (await response.json()) as { accessToken: string };
          accessToken = body.accessToken;
          return 'ok';
        }
        if (response.status === 401 || response.status === 403) return 'invalid';
        if (attempt >= MAX_REFRESH_ATTEMPTS) return 'unavailable';
        await wait(retryAfter(response) ?? backoff(attempt));
      }
    } finally {
      refreshing = null;
    }
  });

  return refreshing;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
// 1s, 2s, 4s: about seven seconds in all, enough to outlast a brief rate limit.
const backoff = (attempt: number) => 1000 * 2 ** (attempt - 1);

/** The server's own Retry-After, capped so a page never hangs on it. */
function retryAfter(response: Response): number | null {
  const seconds = Number(response.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 8) * 1000 : null;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Set on POSTs that cost money or create an appointment. */
  idempotencyKey?: string;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
  retried = false,
  rateLimitRetried = false,
): Promise<T> {
  const { body, idempotencyKey, ...init } = options;

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...init.headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    credentials: 'include',
  });

  if (response.status === 401 && !retried) {
    // TOKEN_EXPIRED refreshes silently. Only a refresh the server refuses ends
    // the session; if it could not be reached, this request fails and the
    // person stays signed in.
    const outcome = await refreshSession();
    if (outcome === 'ok') return request<T>(path, options, true, rateLimitRetried);
    if (outcome === 'invalid') for (const handler of onUnauthenticated) handler();
    else throw new ApiError('UNAVAILABLE', 'Could not reach SkinVizo. Check your connection and try again.', undefined, 503);
  }

  // A read that was rate limited is retried once after the server's own wait.
  // Writes are not: repeating one is the caller's decision.
  if (response.status === 429 && !rateLimitRetried && (init.method ?? 'GET') === 'GET') {
    await wait(retryAfter(response) ?? 1000);
    return request<T>(path, options, retried, true);
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | { error?: { code: string; message: string; details?: Record<string, unknown> } }
      | null;

    throw new ApiError(
      payload?.error?.code ?? 'UNKNOWN',
      payload?.error?.message ?? 'Something went wrong.',
      payload?.error?.details,
      response.status,
    );
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Downloads a file the API produces, with the session's token. A plain link
 * cannot do this: the access token lives in memory, never in a cookie, so an
 * <a href> to the API arrives unauthenticated.
 */
async function download(path: string, filename: string, retried = false): Promise<void> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });

  if (response.status === 401 && !retried) {
    const outcome = await refreshSession();
    if (outcome === 'ok') return download(path, filename, true);
    if (outcome === 'invalid') for (const handler of onUnauthenticated) handler();
  }
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
    throw new ApiError(payload?.error?.code ?? 'UNKNOWN', payload?.error?.message ?? 'Could not download that file.', undefined, response.status);
  }

  const url = URL.createObjectURL(await response.blob());
  const link = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked after the click has been handled, not before.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const http = {
  download,
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>(path, { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
